import {
  IWebsiteBuilderButtonSettings,
  TWebsiteBuilderButtonBorderStyle,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderButton";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import { cloneDeep } from "lodash";
import Checkbox from "components/form/checkbox/Checkbox";
import { WebsiteBuilderColorPicker } from "pages/secure/facility/websiteBuilder/editor/components/colorPicker/WebsiteBuilderColorPicker";
import { Select } from "components/select/index";
import { useAppDispatch } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/websiteBuilder";
import {
  IWebsiteBuilderPopupOption,
  renderWebsiteBuilderPopupOptions,
} from "../../../../helpers/popup/renderWebsiteBuilderPopupOptions";
import { WebsiteBuilderPopupSections } from "../../../../helpers/popup/WebsiteBuilderPopupSections";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-regular-svg-icons";

interface IButtonSettingsProps {
  id: string;
  settings: IWebsiteBuilderButtonSettings;
}

export function ButtonSettings(props: IButtonSettingsProps) {
  const { id, settings } = props;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderButtonSettings>(cloneDeep(settings));

  const borderStyleOptions: IWebsiteBuilderPopupOption<TWebsiteBuilderButtonBorderStyle>[] = useMemo(() => {
    return [
      {
        label: t("Solid"),
        value: "solid",
      },
      {
        label: t("Transparent"),
        value: "transparent",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderButtonSettings, nextSettings: IWebsiteBuilderButtonSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings({
        id,
        prevElementSettings: {
          type: "button",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "button",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        <Input
          label={t("Label")}
          value={updatedSettings.label}
          onChange={e =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              label: e.target.value,
            }))
          }
          autoFocus
        />
        <Select
          label={t("Border Style")}
          defaultValue={updatedSettings.borderStyle}
          onChange={(borderStyle: TWebsiteBuilderButtonBorderStyle) =>
            handleUpdateState(prev => ({
              ...prev,
              borderStyle,
            }))
          }
        >
          {renderWebsiteBuilderPopupOptions(borderStyleOptions)}
        </Select>
        <Checkbox
          size="medium"
          label={t("Rounded Corners")}
          value={updatedSettings.roundedCorners}
          checked={updatedSettings.roundedCorners}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              roundedCorners: e.target.checked,
            }))
          }
        />
        <Input
          label={t("Link")}
          value={updatedSettings.link}
          prefix={<FontAwesomeIcon icon={faLink} />}
          onChange={e =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              link: e.target.value,
            }))
          }
        />
        <WebsiteBuilderColorPicker
          title={t("Text")}
          hexColor={updatedSettings.color}
          updateHexColor={color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              color,
            }))
          }
        />
        <WebsiteBuilderColorPicker
          title={t("Background")}
          hexColor={updatedSettings.backgroundColor}
          updateHexColor={backgroundColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              backgroundColor,
            }))
          }
        />
        <WebsiteBuilderColorPicker
          title={t("Border")}
          hexColor={updatedSettings.borderColor}
          updateHexColor={borderColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              borderColor,
            }))
          }
        />
      </>
    );
  }

  return <WebsiteBuilderPopupSections sections={[renderSettings()]} />;
}
